"use client";

import { cn } from "@/lib/utils";
import { palette } from "@/lib/palette";
import { useWorkshop } from "@/components/workshop-provider";
import type { Employee } from "@/types/workshop";

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase() || "?";
}

export function EmployeeAvatar({ employee, size = "sm", className }: { employee: Employee; size?: "sm" | "md"; className?: string }) {
  const color = palette(employee.color);
  return (
    <span
      title={employee.name}
      className={cn(
        "inline-flex shrink-0 items-center justify-center rounded-full border font-semibold",
        size === "md" ? "h-8 w-8 text-xs" : "h-6 w-6 text-[10px]",
        color.chip,
        className,
      )}
    >
      {initials(employee.name)}
    </span>
  );
}

/** Overlapping initials of a card's assignees. */
export function EmployeeAvatars({ ids, max = 3, className }: { ids: string[]; max?: number; className?: string }) {
  const ws = useWorkshop();
  const people = ids.map((id) => ws.employee(id)).filter((e): e is Employee => !!e);
  if (people.length === 0) return null;
  const extra = people.length - max;
  return (
    <span className={cn("flex -space-x-1.5", className)}>
      {people.slice(0, max).map((e) => (
        <EmployeeAvatar key={e.id} employee={e} className="ring-background ring-2" />
      ))}
      {extra > 0 && (
        <span className="bg-muted ring-background inline-flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-medium ring-2">
          +{extra}
        </span>
      )}
    </span>
  );
}
